/**
 * §7.30 Air-quality severity overlay.
 *
 * Couples ambient PM2.5 levels to respiratory-pathogen mortality via a
 * literature-anchored severity multiplier. The clinical signal is that areas
 * with persistent high PM2.5 see disproportionately higher mortality per
 * respiratory infection (COVID-19 was the cleanest natural-experiment
 * cohort). Operators briefing a respiratory pandemic need to know WHERE the
 * air-quality compound risk is concentrated so escalated triage / oxygen
 * pre-positioning / population-shielding messaging lands in the right cells.
 *
 * ## Literature anchor for the PM2.5 → mortality elasticity
 *
 *   - Wu et al. 2020 (Science Advances, PMID 33148656). Nationwide US
 *     ecological cohort during COVID-19's first wave: a +1 μg/m³ long-term
 *     PM2.5 exposure was associated with an **8% increase in COVID-19
 *     mortality** (MRR 1.08, 95% CI 1.02–1.15). This is the headline
 *     multiplier we apply per +1 μg/m³ above the WHO 2021 AQG threshold of
 *     5 μg/m³.
 *
 *   - Pope et al. 2002 (JAMA, PMID 11879110). Long-term PM2.5 → cardio-
 *     pulmonary mortality (Society of Actuaries / ACS Cancer Prevention II
 *     cohort), the foundational pre-COVID epidemiology. Pope reports
 *     ~6% all-cause mortality risk per +10 μg/m³; broadly compatible with
 *     Wu's per-infection mortality estimate when scaled.
 *
 *   - WHO 2021 Global Air Quality Guidelines: annual mean PM2.5 threshold
 *     revised DOWN from 10 μg/m³ (2005) to **5 μg/m³** (2021). The 2021
 *     value is the no-additional-risk anchor used here; everything above
 *     5 μg/m³ accumulates an attributable-mortality fraction.
 *
 *   - Cohen et al. 2017 (Lancet, PMID 28408086 — GBD 2015 ambient air
 *     pollution analysis) provides the global burden context: ambient
 *     PM2.5 was attributable to 4.2 million deaths globally in 2015.
 *     Background for the magnitude of the compounding signal.
 *
 * ## Why a curated reference snapshot (not live Sentinel-5P / OpenAQ)
 *
 * v1 uses 23 representative regions' annual mean PM2.5 from the
 * **State of Global Air 2023** (Health Effects Institute) and **IQAir
 * World Air Quality Report 2023** — both established open-attribution
 * data sources. We have not yet wired:
 *
 *   - **Copernicus Sentinel-5P TROPOMI** (ESA): NO2 + aerosol-optical-
 *     depth retrievals every 24h at ~7 km resolution. Would let us
 *     surface anomalous spikes (wildfire smoke / dust storms) on a
 *     near-realtime basis. Requires ESA Sentinel API auth + a
 *     PM2.5-from-AOD conversion layer (van Donkelaar regression) that
 *     is region-tunable.
 *   - **OpenAQ** federated open-data: ground-station measurements
 *     aggregated from national EPA-equivalents. Patchy coverage
 *     (excellent in OECD, sparse in sub-Saharan Africa / LMICs).
 *   - **WAQI** (World Air Quality Index) — has a tile API but
 *     attribution rules are restrictive.
 *
 * When a live ingest is wired (Phase 13+) the same surface consumes
 * the live PM2.5 series; the multiplier model + UI do not change.
 *
 * ## Multiplier computation
 *
 *   attributableMultiplier = 1 + (max(pm25 - 5, 0) × 0.08)
 *
 * I.e. for every 1 μg/m³ above the WHO 2021 AQG threshold, mortality
 * is scaled up by 8% (additive). Capped at 5.0× as a guardrail against
 * runaway extrapolation past the cohort range (Wu's cohort reached
 * ~25 μg/m³; we extrapolate cautiously to the worst-cell ~100 μg/m³
 * megacity values but cap the resulting MRR).
 *
 *   attributableDeaths = baselineDeaths × attributableMultiplier - baselineDeaths
 *
 * Where baselineDeaths = population × attackRate × IFR over the scenario
 * duration; operator-tunable.
 */

import { useMemo, useState } from 'react';
import { pinForBrief } from '../pack/pin-to-brief';
import { SurfaceScaffold } from '../SurfaceScaffold';
import { HonestyRail } from '../HonestyRail';

interface RespiratoryPathogen {
  id: string;
  label: string;
  /** Headline infection-fatality ratio (all-ages, population-weighted)
   *  used as the baseline mortality input. Catalog-aligned where
   *  possible; sources cited per-pathogen. */
  ifr: number;
  /** Default scenario attack rate over the duration. Operator-tunable
   *  in the surface; this is the prior. */
  defaultAttackRatePct: number;
  ifrSource: string;
}

const RESPIRATORY_PATHOGENS: readonly RespiratoryPathogen[] = [
  {
    id: 'sars-cov-2',
    label: 'COVID-19 (ancestral)',
    ifr: 0.0068,
    defaultAttackRatePct: 30,
    ifrSource: 'O\'Driscoll 2021 (PMID 33137809) — age-aggregated population-weighted',
  },
  {
    id: 'influenza-a-h1n1',
    label: 'Influenza A (pandemic H1N1)',
    ifr: 0.0002,
    defaultAttackRatePct: 25,
    ifrSource: 'Wong 2013 (PMID 24235139) — global meta-analysis',
  },
  {
    id: 'influenza-a-h5n1',
    label: 'Influenza A (H5N1, hypothetical pandemic)',
    ifr: 0.30,
    defaultAttackRatePct: 20,
    ifrSource: 'WHO HPAI cumulative 2003-2024 (case-based, severe surveillance bias)',
  },
  {
    id: 'rsv',
    label: 'RSV (seasonal)',
    ifr: 0.0001,
    defaultAttackRatePct: 15,
    ifrSource: 'Shi 2017 (Lancet, PMID 28689664) — global burden',
  },
  {
    id: 'measles',
    label: 'Measles',
    ifr: 0.002,
    defaultAttackRatePct: 35,
    ifrSource: 'Wolfson 2009 (Lancet, PMID 19101358) — under-5 endemic settings',
  },
];

interface PmRegion {
  id: string;
  label: string;
  /** Annual mean PM2.5 in μg/m³. Source-of-record blended from State of
   *  Global Air 2023 (HEI) + IQAir World Air Quality Report 2023. */
  pm25: number;
  /** Approximate metropolitan population in millions, used for the
   *  default attributable-deaths denominator. Rough city-region figures. */
  populationMillions: number;
  source: 'IQAir 2023' | 'SoGA 2023' | 'EEA 2023' | 'US EPA 2023';
}

const PM_REGIONS: readonly PmRegion[] = [
  { id: 'new-delhi',     label: 'New Delhi, India',         pm25: 100, populationMillions: 32,   source: 'IQAir 2023' },
  { id: 'dhaka',         label: 'Dhaka, Bangladesh',        pm25: 80,  populationMillions: 23,   source: 'IQAir 2023' },
  { id: 'lahore',        label: 'Lahore, Pakistan',         pm25: 75,  populationMillions: 14,   source: 'IQAir 2023' },
  { id: 'karachi',       label: 'Karachi, Pakistan',        pm25: 70,  populationMillions: 17,   source: 'IQAir 2023' },
  { id: 'cairo',         label: 'Cairo, Egypt',             pm25: 70,  populationMillions: 22,   source: 'SoGA 2023' },
  { id: 'mumbai',        label: 'Mumbai, India',            pm25: 55,  populationMillions: 21,   source: 'IQAir 2023' },
  { id: 'beijing',       label: 'Beijing, China',           pm25: 50,  populationMillions: 22,   source: 'IQAir 2023' },
  { id: 'lagos',         label: 'Lagos, Nigeria',           pm25: 45,  populationMillions: 15,   source: 'SoGA 2023' },
  { id: 'kinshasa',      label: 'Kinshasa, DRC',            pm25: 45,  populationMillions: 17,   source: 'SoGA 2023' },
  { id: 'jakarta',       label: 'Jakarta, Indonesia',       pm25: 40,  populationMillions: 11,   source: 'IQAir 2023' },
  { id: 'shanghai',      label: 'Shanghai, China',          pm25: 35,  populationMillions: 28,   source: 'IQAir 2023' },
  { id: 'tehran',        label: 'Tehran, Iran',             pm25: 30,  populationMillions: 9,    source: 'IQAir 2023' },
  { id: 'mexico-city',   label: 'Mexico City, Mexico',      pm25: 25,  populationMillions: 22,   source: 'IQAir 2023' },
  { id: 'seoul',         label: 'Seoul, South Korea',       pm25: 25,  populationMillions: 26,   source: 'IQAir 2023' },
  { id: 'moscow',        label: 'Moscow, Russia',           pm25: 18,  populationMillions: 13,   source: 'IQAir 2023' },
  { id: 'paris',         label: 'Paris, France',            pm25: 15,  populationMillions: 11,   source: 'EEA 2023' },
  { id: 'st-petersburg', label: 'St Petersburg, Russia',    pm25: 15,  populationMillions: 5.6,  source: 'IQAir 2023' },
  { id: 'tokyo',         label: 'Tokyo, Japan',             pm25: 12,  populationMillions: 37,   source: 'IQAir 2023' },
  { id: 'london',        label: 'London, UK',               pm25: 12,  populationMillions: 9.5,  source: 'EEA 2023' },
  { id: 'los-angeles',   label: 'Los Angeles, USA',         pm25: 12,  populationMillions: 13,   source: 'US EPA 2023' },
  { id: 'melbourne',     label: 'Melbourne, Australia',     pm25: 8,   populationMillions: 5.0,  source: 'IQAir 2023' },
  { id: 'sydney',        label: 'Sydney, Australia',        pm25: 7,   populationMillions: 5.3,  source: 'IQAir 2023' },
  { id: 'new-york',      label: 'New York, USA',            pm25: 7,   populationMillions: 19,   source: 'US EPA 2023' },
];

/** WHO 2021 Global Air Quality Guidelines annual mean PM2.5 threshold. */
const WHO_AQG_2021_PM25 = 5;
/** Per +1 μg/m³ above the WHO AQG, mortality risk multiplier increase.
 *  Wu et al. 2020, Science Advances PMID 33148656 (COVID-19 cohort). */
const PM25_MORTALITY_ELASTICITY = 0.08;
/** Cap on the multiplier — guardrail against extrapolation past the
 *  Wu cohort range (~25 μg/m³). */
const MULTIPLIER_CEILING = 5.0;

function computeMultiplier(pm25: number): number {
  const excess = Math.max(pm25 - WHO_AQG_2021_PM25, 0);
  return Math.min(1 + excess * PM25_MORTALITY_ELASTICITY, MULTIPLIER_CEILING);
}

function formatDeaths(n: number): string {
  if (n < 1000) return n.toFixed(0);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

export function AirQualityOverlay() {
  const [pathogenId, setPathogenId] = useState<string>(RESPIRATORY_PATHOGENS[0]!.id);
  const [regionId, setRegionId] = useState<string>(PM_REGIONS[0]!.id);
  const [durationDays, setDurationDays] = useState<number>(180);
  const [attackRatePctOverride, setAttackRatePctOverride] = useState<number | null>(null);

  const pathogen = RESPIRATORY_PATHOGENS.find(p => p.id === pathogenId)!;
  const region = PM_REGIONS.find(r => r.id === regionId)!;
  const attackRatePct = attackRatePctOverride ?? pathogen.defaultAttackRatePct;

  const result = useMemo(() => {
    const population = region.populationMillions * 1_000_000;
    const infections = population * (attackRatePct / 100);
    const baselineDeaths = infections * pathogen.ifr;
    const multiplier = computeMultiplier(region.pm25);
    const compoundedDeaths = baselineDeaths * multiplier;
    const attributableDeaths = compoundedDeaths - baselineDeaths;
    return { population, infections, baselineDeaths, multiplier, compoundedDeaths, attributableDeaths };
  }, [pathogen, region, attackRatePct]);

  /** Per-region comparison table — fixed pathogen, default attack rate. */
  const allRegionComparison = useMemo(() => {
    return PM_REGIONS.map(r => {
      const population = r.populationMillions * 1_000_000;
      const infections = population * (pathogen.defaultAttackRatePct / 100);
      const baselineDeaths = infections * pathogen.ifr;
      const multiplier = computeMultiplier(r.pm25);
      const attributableDeaths = baselineDeaths * (multiplier - 1);
      return {
        region: r,
        multiplier,
        baselineDeaths,
        attributableDeaths,
      };
    }).sort((a, b) => b.attributableDeaths - a.attributableDeaths);
  }, [pathogen]);

  const handlePin = () => {
    pinForBrief({
      id: `air-quality-${pathogenId}-${regionId}-${durationDays}d-${attackRatePct}ar`,
      surface: '§7.30',
      label: `${pathogen.label} · ${region.label} · +${formatDeaths(result.attributableDeaths)} air-quality-attributable deaths`,
      value: result.attributableDeaths,
      context: {
        pathogenId,
        regionId,
        pm25: region.pm25,
        multiplier: result.multiplier,
        baselineDeaths: result.baselineDeaths,
        compoundedDeaths: result.compoundedDeaths,
        durationDays,
        attackRatePct,
        elasticitySource: 'Wu 2020 (PMID 33148656)',
        thresholdSource: 'WHO AQG 2021 (5 μg/m³)',
      },
    });
  };

  /** PM2.5 tier banding for the per-region table colouring. WHO AQG
   *  + interim targets: <5 (AQG), 5-10 (IT-4), 10-15 (IT-3), 15-25
   *  (IT-2), 25-35 (IT-1), >35 (well above IT-1). */
  function pm25Color(pm: number): string {
    if (pm <= 5) return 'var(--bm-good, #6cbf6e)';
    if (pm <= 15) return 'var(--bm-text)';
    if (pm <= 35) return 'var(--bm-warn, #d4a857)';
    return 'var(--bm-danger, #e56666)';
  }

  return (
    <SurfaceScaffold
      section="§7.30"
      title="Air-quality severity overlay"
      subtitle={
        <>
          PM2.5 ambient pollution × respiratory-pathogen IFR · WHO 2021 AQG anchor (5 μg/m³) ·
          {' '}Wu 2020 mortality elasticity (8% per +1 μg/m³) · 23 reference regions
        </>
      }
      honestyRail={
        <>
          <HonestyRail variant="synthetic">
            Modelled PM2.5 × IFR association, not a causal estimate. Wu 2020 cohort reached
            ~25 μg/m³ — multiplier extrapolation above that is capped at {MULTIPLIER_CEILING.toFixed(1)}×
            to avoid runaway projections.
          </HonestyRail>
          <HonestyRail variant="limitation">
            Reference snapshot, not live ingest. PM2.5 values are annual-mean snapshots from
            State of Global Air 2023 (HEI) + IQAir World Air Quality Report 2023 + EEA / US EPA
            national equivalents. Live Sentinel-5P TROPOMI + OpenAQ federated ingest is a
            future-phase wiring. When live data lands (Phase 13+) the same surface consumes the
            live series; multiplier + UI do not change.
          </HonestyRail>
        </>
      }
      kpis={
        <div className="panel">
          <div className="panel__head"><span className="panel__title">Compounded mortality — {region.label} · {pathogen.label}</span></div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, padding: 14 }}>
            <div>
              <div style={{ fontSize: 10.5, color: 'var(--bm-text-subtle)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Ambient PM2.5</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: pm25Color(region.pm25) }}>{region.pm25}</div>
              <div style={{ fontSize: 11, color: 'var(--bm-text-muted)' }}>μg/m³ · WHO AQG {WHO_AQG_2021_PM25}</div>
            </div>
            <div>
              <div style={{ fontSize: 10.5, color: 'var(--bm-text-subtle)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Mortality multiplier</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: result.multiplier > 2 ? 'var(--bm-danger, #e56666)' : result.multiplier > 1.4 ? 'var(--bm-warn, #d4a857)' : 'var(--bm-text)' }}>
                {result.multiplier.toFixed(2)}×
              </div>
              <div style={{ fontSize: 11, color: 'var(--bm-text-muted)' }}>vs WHO AQG baseline</div>
            </div>
            <div>
              <div style={{ fontSize: 10.5, color: 'var(--bm-text-subtle)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Baseline deaths</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--bm-text)' }}>
                {formatDeaths(result.baselineDeaths)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--bm-text-muted)' }}>at WHO-AQG air quality</div>
            </div>
            <div>
              <div style={{ fontSize: 10.5, color: 'var(--bm-text-subtle)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Air-quality attributable</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--bm-danger, #e56666)' }}>
                +{formatDeaths(result.attributableDeaths)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--bm-text-muted)' }}>excess deaths · pin to brief</div>
            </div>
          </div>
          <div style={{ padding: '0 14px 14px', fontSize: 11.5, color: 'var(--bm-text-muted)', lineHeight: 1.55 }}>
            Compounded total: <strong style={{ color: 'var(--bm-text)' }}>{formatDeaths(result.compoundedDeaths)}</strong> deaths over {durationDays} days
            ({attackRatePct}% attack rate × {(pathogen.ifr * 100).toFixed(3)}% IFR × {region.populationMillions.toFixed(1)}M population × {result.multiplier.toFixed(2)}× air-quality scaling).
            Multiplier formula: <code>1 + max(PM2.5 - {WHO_AQG_2021_PM25}, 0) × {PM25_MORTALITY_ELASTICITY}</code> (Wu et al. 2020, PMID 33148656).
          </div>
          <div style={{ padding: '0 14px 14px' }}>
            <button
              type="button"
              onClick={handlePin}
              style={{
                padding: '6px 12px', fontSize: 12, fontWeight: 600,
                background: 'var(--bm-accent, #6ea3ff)', color: 'var(--bm-bg, #fff)',
                border: 'none', borderRadius: 4, cursor: 'pointer',
              }}
            >
              Pin attributable deaths to brief
            </button>
          </div>
        </div>
      }
      inputs={
        <div className="panel">
          <div className="panel__head"><span className="panel__title">Scenario inputs</span></div>
          <div className="simctl">
            <label className="simctl__field">
              <span className="simctl__label">Respiratory pathogen</span>
              <select value={pathogenId} onChange={e => { setPathogenId(e.target.value); setAttackRatePctOverride(null); }}>
                {RESPIRATORY_PATHOGENS.map(p => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
              <span style={{ fontSize: 10.5, color: 'var(--bm-text-subtle)', marginTop: 4 }}>
                IFR {(pathogen.ifr * 100).toFixed(pathogen.ifr < 0.001 ? 3 : 2)}% · {pathogen.ifrSource}
              </span>
            </label>
            <label className="simctl__field">
              <span className="simctl__label">Region (annual mean PM2.5)</span>
              <select value={regionId} onChange={e => setRegionId(e.target.value)}>
                {PM_REGIONS.map(r => (
                  <option key={r.id} value={r.id}>{r.label} — {r.pm25} μg/m³</option>
                ))}
              </select>
              <span style={{ fontSize: 10.5, color: 'var(--bm-text-subtle)', marginTop: 4 }}>
                Pop ≈ {region.populationMillions.toFixed(1)}M · source: {region.source}
              </span>
            </label>
            <label className="simctl__field">
              <span className="simctl__label">Scenario duration · days</span>
              <select value={durationDays} onChange={e => setDurationDays(parseInt(e.target.value, 10))}>
                <option value="30">30 days (acute wave)</option>
                <option value="90">90 days (single quarter)</option>
                <option value="180">180 days (single season)</option>
                <option value="365">365 days (full year)</option>
              </select>
            </label>
            <label className="simctl__field">
              <span className="simctl__label">Cumulative attack rate · %</span>
              <input
                type="number" min="1" max="100" step="1"
                value={attackRatePct}
                onChange={e => setAttackRatePctOverride(parseFloat(e.target.value) || 0)}
              />
              <span style={{ fontSize: 10.5, color: 'var(--bm-text-subtle)', marginTop: 4 }}>
                Pathogen default: {pathogen.defaultAttackRatePct}%
              </span>
            </label>
          </div>
        </div>
      }
    >
      <div className="panel">
        <div className="panel__head"><span className="panel__title">Region comparison · {pathogen.label} at default {pathogen.defaultAttackRatePct}% attack rate</span></div>
        <table className="dtable">
          <thead>
            <tr>
              <th>Region</th>
              <th className="num">PM2.5 μg/m³</th>
              <th className="num">Multiplier</th>
              <th className="num">Pop (M)</th>
              <th className="num">Baseline deaths</th>
              <th className="num">Air-quality attributable</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {allRegionComparison.map(row => (
              <tr key={row.region.id} style={{ background: row.region.id === regionId ? 'var(--bm-row-selected, rgba(110,163,255,0.08))' : undefined }}>
                <td><strong>{row.region.label}</strong></td>
                <td className="num" style={{ color: pm25Color(row.region.pm25), fontWeight: 600 }}>
                  {row.region.pm25}
                </td>
                <td className="num" style={{ fontWeight: 600 }}>{row.multiplier.toFixed(2)}×</td>
                <td className="num">{row.region.populationMillions.toFixed(1)}</td>
                <td className="num">{formatDeaths(row.baselineDeaths)}</td>
                <td className="num" style={{ color: 'var(--bm-danger, #e56666)', fontWeight: 600 }}>
                  +{formatDeaths(row.attributableDeaths)}
                </td>
                <td style={{ fontSize: 10.5, color: 'var(--bm-text-subtle)' }}>{row.region.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">How this surface plugs into decision support</span></div>
        <ol style={{ padding: '12px 14px 12px 32px', margin: 0, fontSize: 12.5, lineHeight: 1.6, color: 'var(--bm-text-muted)' }}>
          <li><strong>§7.3 Per-region drilldown</strong> surfaces ambient PM2.5 as a contextual indicator alongside health-system capacity for any wired region.</li>
          <li><strong>§7.6 Healthcare Capacity</strong> consumes the multiplier to scale ICU + ventilator demand projections in high-PM2.5 cells — oxygen pre-positioning lands faster.</li>
          <li><strong>§7.5 Intervention Planner</strong> can model population-shielding NPIs (mask uptake / outdoor-activity advisories) whose marginal benefit is amplified in high-PM2.5 cells.</li>
          <li><strong>§7.19 Cost-per-DALY</strong> ingests the air-quality-attributable DALYs as a co-burden — long-term clean-air investment becomes pandemic-preparedness investment.</li>
          <li><strong>§7.25 Minister Briefing Pack</strong> PHIA-tags the attributable-deaths figure (high analytical confidence on the elasticity, medium on extrapolation to non-cohort cities) and surfaces it in the Defence Pack as a known co-burden.</li>
        </ol>
      </div>
    </SurfaceScaffold>
  );
}
